import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchAll, type SearchResult } from '@/lib/data';

const typeMeta: Record<SearchResult['type'], { label: string; icon: string }> = {
  movie: { label: '电影', icon: 'ri-film-line' },
  review: { label: '影评', icon: 'ri-quill-pen-line' },
  news: { label: '资讯', icon: 'ri-newspaper-line' },
};

interface SearchBoxProps {
  variant?: 'header' | 'panel';
  placeholder?: string;
  onNavigate?: () => void;
}

export default function SearchBox({ variant = 'header', placeholder = '搜索电影 / 导演 / 影评 / 资讯', onNavigate }: SearchBoxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setResults(query.trim() ? searchAll(query) : []);
    setOpen(Boolean(query.trim()));
  }, [query]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const go = (to: string) => {
    navigate(to);
    setOpen(false);
    setQuery('');
    onNavigate?.();
  };

  return (
    <div ref={containerRef} className="relative w-full">
      <div
        className={`flex items-center gap-2 rounded-md border bg-background-50 px-3 transition-colors ${
          variant === 'header'
            ? 'border-background-200/80 focus-within:border-primary-400 h-10'
            : 'border-background-200/80 focus-within:border-primary-400 h-11'
        }`}
      >
        <i className="ri-search-line text-foreground-400 text-base" aria-hidden="true" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => query.trim() && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && results.length > 0) go(results[0].to);
            if (e.key === 'Escape') setOpen(false);
          }}
          placeholder={placeholder}
          className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
          aria-label="站内搜索"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery('')}
            className="w-5 h-5 flex items-center justify-center text-foreground-400 hover:text-foreground-700 cursor-pointer"
            aria-label="清除搜索"
          >
            <i className="ri-close-line" aria-hidden="true" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[420px] overflow-y-auto rounded-lg border border-background-200 bg-background-50 shadow-none">
          <p className="px-3 py-2 font-label text-[11px] uppercase tracking-wider text-foreground-400 border-b border-background-100">
            站内搜索结果（前端模拟）
          </p>
          {results.length === 0 ? (
            <div className="px-3 py-6 text-center">
              <p className="text-sm text-foreground-500">没有找到与「{query}」相关的内容</p>
              <p className="mt-1 text-xs text-foreground-400">试试输入电影名、导演或关键词</p>
            </div>
          ) : (
            <ul>
              {results.map((r) => (
                <li key={`${r.type}-${r.id}`}>
                  <button
                    type="button"
                    onClick={() => go(r.to)}
                    className="w-full flex items-start gap-3 px-3 py-2.5 text-left hover:bg-primary-50/70 transition-colors cursor-pointer"
                  >
                    <span className="mt-0.5 w-7 h-7 shrink-0 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className={typeMeta[r.type].icon} aria-hidden="true" />
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2">
                        <span className="block text-sm font-medium text-foreground-900 line-clamp-1">{r.title}</span>
                        <span className="shrink-0 rounded-sm bg-accent-100 px-1.5 py-0.5 font-label text-[10px] text-accent-900">
                          {typeMeta[r.type].label}
                        </span>
                      </span>
                      <span className="mt-0.5 block text-xs text-foreground-500 line-clamp-1">{r.subtitle}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}